// ============================================================================
// SEGUIMIENTOS (C4-D2) — la bandeja de "a quién hay que volver a escribirle".
//
// Es la pantalla que justifica el proyecto: GPT Maker no podía iniciar
// conversaciones, así que cada "te confirmo el lunes" se perdía. Aquí no se
// pierde ninguno (I-C4-1): o sale solo, o queda escrito como tarea con su razón.
//
// Prioridad visual, en este orden: lo VENCIDO (ya se pasó la hora), luego lo de
// hoy, luego lo que viene. Las tareas manuales van marcadas porque son las que
// exigen que un humano haga algo.
// ============================================================================
import { useCallback, useEffect, useMemo, useState } from "react";
import { AlarmClock, Bot, Check, User, X } from "lucide-react";
import { Aviso, Boton, Chip, SkeletonLista, Vacio } from "./piezas.tsx";
import { explicarError, listarSeguimientos, resolverSeguimiento } from "./datos.ts";
import { nombreMostrado, type ContextoPanel, type Seguimiento } from "./tipos.ts";

/** "vencido hace 2 h", "hoy 3:00 p. m.", "mañana", "el lunes 4 de agosto". */
export function cuandoVence(iso: string, ahora: Date = new Date()): string {
  const v = new Date(iso);
  const diffMin = Math.round((v.getTime() - ahora.getTime()) / 60_000);
  const hora = new Intl.DateTimeFormat("es-CO", { hour: "numeric", minute: "2-digit" }).format(v);

  if (diffMin < 0) {
    const min = -diffMin;
    if (min < 60) return `vencido hace ${min} min`;
    const h = Math.round(min / 60);
    if (h < 24) return `vencido hace ${h} h`;
    return `vencido hace ${Math.round(h / 24)} días`;
  }
  const hoy = ahora.toDateString() === v.toDateString();
  if (hoy) return `hoy ${hora}`;
  const manana = new Date(ahora.getTime() + 86_400_000);
  if (manana.toDateString() === v.toDateString()) return `mañana ${hora}`;
  return new Intl.DateTimeFormat("es-CO", { weekday: "long", day: "numeric", month: "long" }).format(v);
}

function Tarjeta({
  s, onResolver, ocupado,
}: {
  s: Seguimiento;
  onResolver: (id: string, estado: "hecho" | "cancelado") => void;
  ocupado: boolean;
}) {
  const nombre = nombreMostrado({ nombre: s.nombre, telefono_e164: s.telefono_e164, wa_id: s.wa_id } as any);
  const manual = s.estado === "manual";
  const razon = typeof s.detalle?.motivo === "string" ? s.detalle.motivo : null;

  return (
    <li className={`border rounded-xl p-3 bg-superficie transition-colors
      ${s.vencido || manual ? "border-marca/40" : "border-borde"}`}>
      <div className="flex items-start gap-2 flex-wrap">
        <span className="font-medium text-[14.5px] truncate">{nombre}</span>
        <Chip tono={s.creado_por === "bot" ? "marca" : "neutro"}>
          {s.creado_por === "bot" ? <><Bot size={10} /> Bot</> : <><User size={10} /> Asesor</>}
        </Chip>
        {manual && <Chip tono="alerta">Escríbele tú</Chip>}
        <span className={`ml-auto text-[12px] cifra whitespace-nowrap
          ${s.vencido || manual ? "text-marca font-medium" : "text-tinta-2"}`}>
          <AlarmClock size={11} className="inline mb-px mr-1" />
          {cuandoVence(s.vence)}
        </span>
      </div>

      <p className="text-[13.5px] text-tinta mt-1.5">{s.motivo}</p>
      {s.mensaje_sugerido && (
        <p className="text-[13px] text-tinta-2 mt-1 pl-2.5 border-l-2 border-borde italic">
          “{s.mensaje_sugerido}”
        </p>
      )}
      {manual && razon && (
        <p className="text-[12.5px] text-tinta-2 mt-1.5">
          No se pudo mandar solo: {razon}.
        </p>
      )}

      <div className="flex gap-2 mt-2.5">
        <Boton tono="marca" onClick={() => onResolver(s.id, "hecho")} disabled={ocupado}
          className="h-8 text-[13px]">
          <Check size={14} /> Ya lo hice
        </Boton>
        <Boton onClick={() => onResolver(s.id, "cancelado")} disabled={ocupado}
          className="h-8 text-[13px]">
          <X size={14} /> Ya no aplica
        </Boton>
      </div>
    </li>
  );
}

export function Seguimientos({ ctx }: { ctx: ContextoPanel }) {
  const { sb, tenantId } = ctx;
  const [lista, setLista] = useState<Seguimiento[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);

  const recargar = useCallback(async () => {
    try {
      setLista(await listarSeguimientos(sb, tenantId));
      setError(null);
    } catch (e) {
      setError(explicarError(e));
    } finally {
      setCargando(false);
    }
  }, [sb, tenantId]);

  useEffect(() => { void recargar(); }, [recargar]);

  // Vencidos y tareas manuales arriba: son los que cuestan plata si se olvidan.
  const { urgentes, proximos } = useMemo(() => {
    const urg = lista.filter((s) => s.vencido || s.estado === "manual");
    const prox = lista.filter((s) => !s.vencido && s.estado !== "manual");
    return { urgentes: urg, proximos: prox };
  }, [lista]);

  async function resolver(id: string, estado: "hecho" | "cancelado") {
    setOcupado(id);
    // Optimista: la tarjeta se va de una. Si la base rechaza, vuelve y se avisa.
    const antes = lista;
    setLista((p) => p.filter((s) => s.id !== id));
    try {
      await resolverSeguimiento(sb, id, estado);
    } catch (e) {
      setLista(antes);
      setError(explicarError(e));
    } finally {
      setOcupado(null);
    }
  }

  return (
    <div className="h-full flex flex-col min-w-0">
      <header className="shrink-0 px-4 sm:px-6 pt-4 pb-3 border-b border-borde-suave">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-[19px] font-semibold tracking-tight">Seguimientos</h1>
          {urgentes.length > 0 && (
            <Chip tono="alerta">{urgentes.length} por atender</Chip>
          )}
        </div>
        <p className="text-[13px] text-tinta-2 mt-0.5">
          A quién hay que volver a escribirle. Lo agenda el bot en plena conversación o tú a mano.
        </p>
      </header>

      <div className="flex-1 overflow-y-auto scroll-fino px-4 sm:px-6 py-4">
        <div className="max-w-[720px] mx-auto w-full space-y-5">
          {error && <Aviso tono="peligro" onCerrar={() => setError(null)}>{error}</Aviso>}

          {cargando && <SkeletonLista filas={3} />}

          {!cargando && lista.length === 0 && (
            <Vacio titulo="Nada pendiente por ahora">
              Cuando el bot quede de escribirle a alguien —o cuando tú lo agendes desde la ficha del
              cliente— aparecerá aquí.
            </Vacio>
          )}

          {urgentes.length > 0 && (
            <section>
              <h2 className="text-[12px] uppercase tracking-wide text-tinta-2 mb-2">Para hoy</h2>
              <ul className="space-y-2">
                {urgentes.map((s) => (
                  <Tarjeta key={s.id} s={s} onResolver={resolver} ocupado={ocupado === s.id} />
                ))}
              </ul>
            </section>
          )}

          {proximos.length > 0 && (
            <section>
              <h2 className="text-[12px] uppercase tracking-wide text-tinta-2 mb-2">Más adelante</h2>
              <ul className="space-y-2">
                {proximos.map((s) => (
                  <Tarjeta key={s.id} s={s} onResolver={resolver} ocupado={ocupado === s.id} />
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
